import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, Row, Col, FormGroup, Label, Input, Modal, ModalHeader, ModalBody, ModalFooter, FormFeedback } from 'reactstrap';
import DataTable from '../../../Common/DataTable';
import { Btn } from '../../../../AbstractElements';
import { DollarSign, FileText, Lock } from 'react-feather';
import { toast } from 'react-toastify';
import { closeAccidentFile, fetchAccidentFilesReadyForArbitration, createArbitrationWithFee, fetchFormData, createFinalCompensation } from '../../../../Api/ApiMethods';
import { shouldToastError } from '../../../../Api/utils';
import { normalizeFieldValue, displayDecimal } from '../../../../_helper/regexHandler';
import PaymentCollectionDataModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionDataModal';
import PaymentCollectionListModal from '../../FinancialOperations/PaymentCollections/PaymentCollectionListModal';

const TodoTabArbitrationReady = ({ onCountChange, onLoadingChange }) => {
    const isInitialLoad = useRef(true);
    const searchRef = useRef({
        applicationDateSearch: '',
        accidentDateSearch: '',
        tcIdSearch: ''
    });

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRows, setTotalRows] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const [applicationDateSearch, setApplicationDateSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [tcIdSearch, setTcIdSearch] = useState('');

    const [isCloseFileModalOpen, setIsCloseFileModalOpen] = useState(false);
    const [currentCloseFileId, setCurrentCloseFileId] = useState(null);
    const [fileStatus, setFileStatus] = useState('Kapalı');
    const [fileCloseDescription, setFileCloseDescription] = useState('');
    const [fileCloseComment, setFileCloseComment] = useState('');
    const [fileCloseErrors, setFileCloseErrors] = useState({});
    const [isClosingFile, setIsClosingFile] = useState(false);

    const [isApplicationFeeModalOpen, setIsApplicationFeeModalOpen] = useState(false);
    const [currentApplicationFileId, setCurrentApplicationFileId] = useState(null);
    const [arbitrationApplicationFee, setArbitrationApplicationFee] = useState('');
    const [arbitrationCode, setArbitrationCode] = useState('');
    const [arbitrationApplicationDate, setArbitrationApplicationDate] = useState('');
    const [arbitrationCaseNumber, setArbitrationCaseNumber] = useState('');
    const [arbitrationPriceForNow, setArbitrationPriceForNow] = useState('');
    const [applicationFeeErrors, setApplicationFeeErrors] = useState({});
    const [isSubmittingApplicationFee, setIsSubmittingApplicationFee] = useState(false);

    // Tahsilat listesi modal state
    const [isPaymentCollectionListModalOpen, setIsPaymentCollectionListModalOpen] = useState(false);
    const [paymentCollections, setPaymentCollections] = useState([]);
    const [isPaymentCollectionsLoading, setIsPaymentCollectionsLoading] = useState(false);
    const [currentAccidentFileForPayments, setCurrentAccidentFileForPayments] = useState(null);

    // Tahsilat ekle/düzenle modal state
    const [paymentCollectionModalOpen, setPaymentCollectionModalOpen] = useState(false);
    const [paymentCollectionModalMode, setPaymentCollectionModalMode] = useState('add');
    const [selectedPaymentCollection, setSelectedPaymentCollection] = useState(null);

    // Kapak gönderim loading state
    const [finalCompensationSubmittingId, setFinalCompensationSubmittingId] = useState(null);

    const paymentCollectionApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/paymentcollection';

    useEffect(() => {
        if (typeof onCountChange === 'function') {
            onCountChange(totalRows);
        }
    }, [onCountChange, totalRows]);

    useEffect(() => {
        if (typeof onLoadingChange === 'function') {
            onLoadingChange(loading);
        }
    }, [loading, onLoadingChange]);

    useEffect(() => {
        searchRef.current = {
            applicationDateSearch,
            accidentDateSearch,
            tcIdSearch
        };
    }, [applicationDateSearch, accidentDateSearch, tcIdSearch]);

    const fetchArbitrationData = useCallback(async () => {
        const apiEndpoint = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/AccidentFile';
        if (!apiEndpoint) {
            return;
        }

        setLoading(true);
        try {
            const queryParams = new URLSearchParams();

            const {
                applicationDateSearch: currentApplicationDateSearch,
                accidentDateSearch: currentAccidentDateSearch,
                tcIdSearch: currentTcIdSearch
            } = searchRef.current;

            if (currentApplicationDateSearch && currentApplicationDateSearch.trim().length > 0) {
                queryParams.append('applicationDate', currentApplicationDateSearch);
            }

            if (currentAccidentDateSearch && currentAccidentDateSearch.trim().length > 0) {
                queryParams.append('accidentDate', currentAccidentDateSearch);
            }

            if (currentTcIdSearch && currentTcIdSearch.trim().length >= 3) {
                queryParams.append('tcId', currentTcIdSearch.trim());
            }

            const fullEndpoint = queryParams.toString() ? `${apiEndpoint}?${queryParams.toString()}` : apiEndpoint;

            await fetchAccidentFilesReadyForArbitration({
                apiEndpoint: fullEndpoint,
                page,
                pageSize,
                search: null,
                setDataCallback: setItems,
                setTotalRowsCallback: setTotalRows,
            });
        } catch (error) {
            toast.error('Dosyalar alınırken bir hata oluştu.');
            setTotalRows(0);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize]);

    useEffect(() => {
        fetchArbitrationData();
    }, [fetchArbitrationData, page, pageSize]);

    useEffect(() => {
        if (isInitialLoad.current) {
            isInitialLoad.current = false;
            return;
        }

        const hasAnySearch = applicationDateSearch.trim() || accidentDateSearch.trim() || tcIdSearch.trim();
        const timeoutId = setTimeout(() => {
            const hasTcIdSearch = tcIdSearch && tcIdSearch.trim().length > 0;
            const hasValidTcIdSearch = tcIdSearch && tcIdSearch.trim().length >= 3;

            if (hasAnySearch) {
                if (hasTcIdSearch && !hasValidTcIdSearch) {
                    return;
                }
                fetchArbitrationData();
            } else {
                fetchArbitrationData();
            }
        }, 500);

        return () => clearTimeout(timeoutId);
    }, [applicationDateSearch, accidentDateSearch, tcIdSearch, fetchArbitrationData]);

    // ─── Dosya Kapatma ───

    const openCloseFileModal = (fileId) => {
        setCurrentCloseFileId(fileId);
        setFileStatus('Kapalı');
        setFileCloseDescription('');
        setFileCloseComment('');
        setFileCloseErrors({});
        setIsCloseFileModalOpen(true);
    };

    const closeFileModal = () => {
        setIsCloseFileModalOpen(false);
        setCurrentCloseFileId(null);
        setFileStatus('Kapalı');
        setFileCloseDescription('');
        setFileCloseComment('');
        setFileCloseErrors({});
    };

    const handleCloseFile = async () => {
        if (!currentCloseFileId) return;

        const nextErrors = {};
        if (!fileCloseDescription.trim()) {
            nextErrors.fileCloseDescription = 'Kapanma açıklaması zorunludur.';
        }

        if (!fileCloseComment.trim()) {
            nextErrors.fileCloseComment = 'Ek yorum zorunludur.';
        }

        if (Object.keys(nextErrors).length > 0) {
            setFileCloseErrors(nextErrors);
            toast.error('Lütfen zorunlu dosya kapatma bilgilerini doldurun.');
            return;
        }

        setIsClosingFile(true);

        try {
            const response = await closeAccidentFile({
                accidentFileId: currentCloseFileId,
                fileStatus: fileStatus,
                fileCloseDescription: fileCloseDescription.trim(),
                fileCloseComment: fileCloseComment.trim()
            });

            const successMessage = response?.message || 'Dosya durumu başarıyla güncellendi!';
            toast.success(successMessage);
            fetchArbitrationData();
            closeFileModal();
        } catch (error) {
            toast.error('Dosya kapatılırken bir hata oluştu.');
        } finally {
            setIsClosingFile(false);
        }
    };

    // ─── Başvuru Ücreti ───

    const openApplicationFeeModal = (fileId) => {
        setCurrentApplicationFileId(fileId);
        setArbitrationApplicationFee('');
        setArbitrationCode('');
        setArbitrationApplicationDate('');
        setArbitrationCaseNumber('');
        setArbitrationPriceForNow('');
        setApplicationFeeErrors({});
        setIsApplicationFeeModalOpen(true);
    };

    const closeApplicationFeeModal = () => {
        setIsApplicationFeeModalOpen(false);
        setCurrentApplicationFileId(null);
        setArbitrationApplicationFee('');
        setArbitrationCode('');
        setArbitrationApplicationDate('');
        setArbitrationCaseNumber('');
        setArbitrationPriceForNow('');
        setApplicationFeeErrors({});
    };

    const setApplicationFeeField = (field, setter, value) => {
        setter(value);
        if (applicationFeeErrors[field]) {
            setApplicationFeeErrors((prev) => {
                const next = { ...prev };
                delete next[field];
                return next;
            });
        }
    };

    const handleSubmitApplicationFee = async () => {
        if (!currentApplicationFileId) return;

        const nextErrors = {};
        if (!arbitrationApplicationFee.trim()) nextErrors.arbitrationApplicationFee = 'Başvuru ücreti zorunludur.';
        if (!arbitrationCode.trim()) nextErrors.arbitrationCode = 'Kod zorunludur.';
        if (!arbitrationApplicationDate) nextErrors.arbitrationApplicationDate = 'Başvuru tarihi zorunludur.';
        if (!arbitrationCaseNumber.trim()) nextErrors.arbitrationCaseNumber = 'Esas numarası zorunludur.';
        if (!arbitrationPriceForNow.trim()) nextErrors.arbitrationPriceForNow = 'Şimdilik tutarı zorunludur.';

        if (Object.keys(nextErrors).length > 0) {
            setApplicationFeeErrors(nextErrors);
            toast.error('Lütfen zorunlu tahkim başvuru bilgilerini doldurun.');
            return;
        }

        const feeAmount = parseFloat(arbitrationApplicationFee.trim());
        if (Number.isNaN(feeAmount) || feeAmount < 0) {
            setApplicationFeeErrors({ arbitrationApplicationFee: 'Geçerli bir başvuru ücreti giriniz.' });
            toast.error('Lütfen geçerli bir başvuru ücreti giriniz.');
            return;
        }

        const priceForNow = parseFloat(arbitrationPriceForNow.trim());
        if (Number.isNaN(priceForNow) || priceForNow < 0) {
            setApplicationFeeErrors({ arbitrationPriceForNow: 'Geçerli bir şimdilik tutarı giriniz.' });
            toast.error('Lütfen geçerli bir şimdilik tutarı giriniz.');
            return;
        }

        setIsSubmittingApplicationFee(true);

        try {
            const responseData = await createArbitrationWithFee({
                accidentFileId: currentApplicationFileId,
                arbitrationApplicationFee: feeAmount,
                code: arbitrationCode.trim(),
                applicationDate: new Date(arbitrationApplicationDate).toISOString(),
                caseNumber: arbitrationCaseNumber.trim(),
                priceForNow
            });

            if (responseData && responseData.isSuccess !== false) {
                toast.success('Tahkim başvurusu başarıyla kaydedildi!');
                fetchArbitrationData();
                closeApplicationFeeModal();
            } else {
                toast.error(responseData?.message || 'Tahkim başvurusu kaydedilirken bir hata oluştu.');
            }
        } catch (error) {
            toast.error('Tahkim başvurusu kaydedilirken bir hata oluştu.');
        } finally {
            setIsSubmittingApplicationFee(false);
        }
    };

    // ─── Tahsilat + Kapak Akışı ───

    const fetchPaymentCollectionsForAccidentFile = useCallback(async (accidentFileId) => {
        if (!accidentFileId) { setPaymentCollections([]); return; }
        setIsPaymentCollectionsLoading(true);
        const normalizedId = String(accidentFileId);

        try {
            const response = await fetchFormData({
                apiEndpoint: `${paymentCollectionApiUrl}?AccidentFile=${encodeURIComponent(normalizedId)}`,
                page: 1,
                pageSize: 100,
            });

            let collections = [];
            if (response?.isSuccess && Array.isArray(response?.data?.items)) {
                collections = response.data.items;
            } else if (Array.isArray(response?.items)) {
                collections = response.items;
            } else if (Array.isArray(response)) {
                collections = response;
            } else if (response?.data && Array.isArray(response.data)) {
                collections = response.data;
            }
            setPaymentCollections(collections);
        } catch (error) {
            if (shouldToastError(error)) toast.error('Tahsilatlar yüklenirken bir hata oluştu.');
            setPaymentCollections([]);
        } finally {
            setIsPaymentCollectionsLoading(false);
        }
    }, [paymentCollectionApiUrl]);

    const handleOpenPaymentCollectionListModal = (row) => {
        setCurrentAccidentFileForPayments(row);
        fetchPaymentCollectionsForAccidentFile(row.id);
        setIsPaymentCollectionListModalOpen(true);
    };

    const closePaymentCollectionListModal = () => {
        setIsPaymentCollectionListModalOpen(false);
        setPaymentCollectionModalOpen(false);
        setSelectedPaymentCollection(null);
        setPaymentCollections([]);
        setCurrentAccidentFileForPayments(null);
    };

    const handleAddPaymentCollection = () => {
        setSelectedPaymentCollection(null);
        setPaymentCollectionModalMode('add');
        setPaymentCollectionModalOpen(true);
    };

    const handleEditPaymentCollection = (collection) => {
        setSelectedPaymentCollection(collection);
        setPaymentCollectionModalMode('modify');
        setPaymentCollectionModalOpen(true);
    };

    const togglePaymentCollectionModal = () => {
        if (paymentCollectionModalOpen) {
            setPaymentCollectionModalOpen(false);
            setTimeout(() => setSelectedPaymentCollection(null), 300);
        } else {
            setPaymentCollectionModalOpen(true);
        }
    };

    const refreshPaymentCollections = useCallback(() => {
        if (currentAccidentFileForPayments?.id) {
            fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
        }
    }, [fetchPaymentCollectionsForAccidentFile, currentAccidentFileForPayments]);

    const handleSendPaymentCollectionToCover = async (collection) => {
        const collectionId = collection?.id || collection?._id;
        if (!collectionId) { toast.warn('Tahsilat kaydı kimliği bulunamadı.'); return; }
        if (!currentAccidentFileForPayments?.id) { toast.warn('Kaza dosyası kimliği bulunamadı.'); return; }

        setFinalCompensationSubmittingId(collectionId);
        try {
            await createFinalCompensation({
                paymentCollections: [String(collectionId)],
                accidentFile: [String(currentAccidentFileForPayments.id)],
                arbitration: [],
                enforcement: []
            });
            await fetchPaymentCollectionsForAccidentFile(currentAccidentFileForPayments.id);
            fetchArbitrationData();
        } catch {
            // Hata createFinalCompensation içinde handle ediliyor
        } finally {
            setFinalCompensationSubmittingId(null);
        }
    };

    // ─── Arama Handler'ları ───

    const handleApplicationDateChange = (event) => {
        setApplicationDateSearch(event.target.value);
        setPage(1);
    };

    const handleAccidentDateChange = (event) => {
        setAccidentDateSearch(event.target.value);
        setPage(1);
    };

    const handleTcIdChange = (event) => {
        const numericValue = normalizeFieldValue('tcIdSearch', event.target.value);
        setTcIdSearch(numericValue);
        setPage(1);
    };

    // ─── Tablo Kolonları ───

    const accidentFileColumns = useMemo(() => ([
        {
            name: 'Müşteri',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';
                if (customer.customerType === 'Kurumsal') {
                    return customer.companyName || '-';
                }
                return customer.clientName || '-';
            },
            sortable: true,
            grow: 2,
            minWidth: '200px'
        },
        {
            name: 'TC/Vergi No',
            selector: row => {
                const customer = row.customer;
                if (!customer) return '-';
                if (customer.customerType === 'Kurumsal') {
                    return customer.taxNumber || '-';
                }
                return customer.identityNumber || '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Kaza Dosyası No',
            selector: row => row.fileNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Plaka',
            selector: row => row.accident?.licensePlate?.licensePlateNumber || '-',
            sortable: true,
            grow: 1,
            minWidth: '100px'
        },
        {
            name: 'Başvuru Tarihi',
            selector: row => row.insuranceApplicationDate ? new Date(row.insuranceApplicationDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Kaza Tarihi',
            selector: row => row.accident?.accidentDate ? new Date(row.accident.accidentDate).toLocaleDateString('tr-TR') : '-',
            sortable: true,
            grow: 1,
            minWidth: '110px'
        },
        {
            name: 'Talep Edilen İş',
            selector: row => {
                const filesToBeWorked = row.filesToBeWorked || [];
                if (filesToBeWorked.length === 0) return '-';
                const fileTypes = filesToBeWorked
                    .map(file => {
                        const fileTypeArray = file.fileType || [];
                        return fileTypeArray.map(ft => ft.title || '').filter(title => title !== '');
                    })
                    .flat()
                    .join(', ');
                return fileTypes || '-';
            },
            sortable: true,
            grow: 1.5,
            minWidth: '150px'
        },
        {
            name: 'Çalışılan Tutar',
            selector: row => {
                const filesToBeWorked = row.filesToBeWorked || [];
                const total = filesToBeWorked.reduce((sum, file) => {
                    const expense = Number(file?.fileExpense) || 0;
                    return sum + expense;
                }, 0);
                return total > 0 ? `${total.toLocaleString('tr-TR')} ₺` : '-';
            },
            sortable: true,
            grow: 1,
            minWidth: '120px'
        },
        {
            name: 'Tahsilat',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'info',
                        size: 'sm',
                        outline: true,
                        onClick: () => handleOpenPaymentCollectionListModal(row),
                        title: 'Tahsilatları Görüntüle / Ekle'
                    }}
                >
                    <DollarSign size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        },
        {
            name: 'Başvuru Bilgileri',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'success',
                        size: 'sm',
                        outline: true,
                        onClick: () => openApplicationFeeModal(row.id),
                        title: 'Başvuru Bilgileri Gir'
                    }}
                >
                    <FileText size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true
        },
        {
            name: 'Dosya Kapa',
            cell: (row) => (
                <Btn
                    attrBtn={{
                        color: 'danger',
                        size: 'sm',
                        outline: true,
                        onClick: () => openCloseFileModal(row.id),
                        title: 'Dosya Kapa'
                    }}
                >
                    <Lock size={18} />
                </Btn>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            grow: 1,
            minWidth: '110px',
            center: true,
            right: true
        }
    ]), []);

    return (
        <Container fluid>
            <Row>
                <Col sm="12">
                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label>Başvuru Tarihi</Label>
                                <Input
                                    type="date"
                                    value={applicationDateSearch}
                                    onChange={handleApplicationDateChange}
                                    placeholder="Başvuru tarihi seçin"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>Plaka Kaza Tarihi</Label>
                                <Input
                                    type="date"
                                    value={accidentDateSearch}
                                    onChange={handleAccidentDateChange}
                                    placeholder="Kaza tarihi seçin"
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label>TC Kimlik Numarası / Vergi Numarası ile Ara</Label>
                                <Input
                                    type="text"
                                    placeholder="TC kimlik numarası / Vergi numarası..."
                                    value={tcIdSearch}
                                    onChange={handleTcIdChange}
                                    maxLength="11"
                                />
                                {tcIdSearch && tcIdSearch.trim().length > 0 && tcIdSearch.trim().length < 3 && (
                                    <small className="text-muted">En az 3 karakter girmelisiniz</small>
                                )}
                            </FormGroup>
                        </Col>
                    </Row>
                    <DataTable
                        columns={accidentFileColumns}
                        data={items}
                        progressPending={loading}
                        pagination
                        paginationServer
                        stickyActionPosition="right"
                        paginationTotalRows={totalRows}
                        onChangePage={setPage}
                        onChangeRowsPerPage={(newPageSize, newPage) => {
                            setPageSize(newPageSize);
                            setPage(newPage);
                        }}
                        paginationPerPage={pageSize}
                        paginationDefaultPage={page}
                        selectableRows={false}
                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                        noDataComponent={loading ? null : "Tahkime hazır kaza dosyası bulunamadı"}
                    />
                </Col>
            </Row>

            {/* Dosya Kapatma Modalı */}
            <Modal isOpen={isCloseFileModalOpen} toggle={closeFileModal}>
                <ModalHeader toggle={closeFileModal}>Dosya Kapatma</ModalHeader>
                <ModalBody>
                    <p className="text-muted mb-3">
                        Bu işlem dosyayı kapalı duruma getirecektir. Lütfen kapanma açıklamasını seçiniz.
                    </p>
                    <FormGroup>
                        <Label for="fileCloseDescription">Kapanma Açıklaması <span className="text-danger">*</span></Label>
                        <Input
                            type="select"
                            id="fileCloseDescription"
                            value={fileCloseDescription}
                            onChange={(e) => {
                                setFileCloseDescription(e.target.value);
                                setFileCloseErrors(prev => ({ ...prev, fileCloseDescription: '' }));
                            }}
                            invalid={!!fileCloseErrors.fileCloseDescription}
                        >
                            <option value="">Kapanma açıklaması seçin...</option>
                            <option value="Gelen ödeme yeterli">Gelen ödeme yeterli</option>
                            <option value="Aynı yerden eski hasarlı">Aynı yerden eski hasarlı</option>
                            <option value="Ağır hasarlı">Ağır hasarlı</option>
                            <option value="Farklı vekil ödeme almış">Farklı vekil ödeme almış</option>
                            <option value="Farklı vekil tahkime başvuru yapmış">Farklı vekil tahkime başvuru yapmış</option>
                            <option value="Vekalet süresi bitti">Vekalet süresi bitti</option>
                            <option value="Azil">Azil</option>
                            <option value="Teminat limiti dolu">Teminat limiti dolu</option>
                            <option value="Sulh">Sulh</option>
                            <option value="Geri çekme">Geri çekme</option>
                            <option value="Feragat">Feragat</option>
                            <option value="Bilirkişi ücreti yatırılmadı">Bilirkişi ücreti yatırılmadı</option>
                            <option value="Kusur değişmedi">Kusur değişmedi</option>
                            <option value="Bakiye limit çıkmadı">Bakiye limit çıkmadı</option>
                            <option value="Görevsizlik">Görevsizlik</option>
                            <option value="Kısmi dava açıldığından">Kısmi dava açıldığından</option>
                            <option value="Fatura olmadığından">Fatura olmadığından</option>
                            <option value="Derdestlik">Derdestlik</option>
                            <option value="Zaman aşımı">Zaman aşımı</option>
                            <option value="Yanlış başvuru">Yanlış başvuru</option>
                        </Input>
                        <FormFeedback>{fileCloseErrors.fileCloseDescription}</FormFeedback>
                    </FormGroup>
                    <FormGroup>
                        <Label for="fileCloseComment">Ek Yorum <span className="text-danger">*</span></Label>
                        <Input
                            type="textarea"
                            id="fileCloseComment"
                            value={fileCloseComment}
                            onChange={(e) => {
                                setFileCloseComment(e.target.value);
                                setFileCloseErrors(prev => ({ ...prev, fileCloseComment: '' }));
                            }}
                            placeholder="Dosya kapatma ile ilgili ek yorumlarınızı buraya yazın..."
                            rows="3"
                            invalid={!!fileCloseErrors.fileCloseComment}
                        />
                        <FormFeedback>{fileCloseErrors.fileCloseComment}</FormFeedback>
                    </FormGroup>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'secondary', onClick: closeFileModal, disabled: isClosingFile }}>
                        İptal
                    </Btn>
                    <Btn attrBtn={{
                        color: 'danger',
                        onClick: handleCloseFile,
                        disabled: isClosingFile
                    }}>
                        {isClosingFile ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Dosya Kapatılıyor...
                            </>
                        ) : (
                            'Dosyayı Kapat'
                        )}
                    </Btn>
                </ModalFooter>
            </Modal>

            {/* Tahkim Başvuru Modalı */}
            <Modal isOpen={isApplicationFeeModalOpen} toggle={closeApplicationFeeModal} size="lg">
                <ModalHeader toggle={closeApplicationFeeModal}>Tahkim Başvuru Bilgileri</ModalHeader>
                <ModalBody>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationApplicationFee">Başvuru Ücreti (TL) <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="arbitrationApplicationFee"
                                    placeholder="Örn: 1000,50"
                                    value={displayDecimal(arbitrationApplicationFee)}
                                    onChange={(e) => {
                                        const value = normalizeFieldValue('arbitrationApplicationFee', e.target.value);
                                        setApplicationFeeField('arbitrationApplicationFee', setArbitrationApplicationFee, value);
                                    }}
                                    invalid={!!applicationFeeErrors.arbitrationApplicationFee}
                                />
                                <FormFeedback>{applicationFeeErrors.arbitrationApplicationFee}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationCode">Kod <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="arbitrationCode"
                                    placeholder="Tahkim kodu giriniz..."
                                    value={arbitrationCode}
                                    onChange={(e) => setApplicationFeeField('arbitrationCode', setArbitrationCode, e.target.value)}
                                    invalid={!!applicationFeeErrors.arbitrationCode}
                                />
                                <FormFeedback>{applicationFeeErrors.arbitrationCode}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationApplicationDate">Başvuru Tarihi <span className="text-danger">*</span></Label>
                                <Input
                                    type="datetime-local"
                                    id="arbitrationApplicationDate"
                                    value={arbitrationApplicationDate}
                                    onChange={(e) => setApplicationFeeField('arbitrationApplicationDate', setArbitrationApplicationDate, e.target.value)}
                                    invalid={!!applicationFeeErrors.arbitrationApplicationDate}
                                />
                                <FormFeedback>{applicationFeeErrors.arbitrationApplicationDate}</FormFeedback>
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationCaseNumber">Esas Numarası <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="arbitrationCaseNumber"
                                    placeholder="Esas numarasını giriniz..."
                                    value={arbitrationCaseNumber}
                                    onChange={(e) => setApplicationFeeField('arbitrationCaseNumber', setArbitrationCaseNumber, e.target.value)}
                                    invalid={!!applicationFeeErrors.arbitrationCaseNumber}
                                />
                                <FormFeedback>{applicationFeeErrors.arbitrationCaseNumber}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label for="arbitrationPriceForNow">Şimdilik Tutar <span className="text-danger">*</span></Label>
                                <Input
                                    type="text"
                                    id="arbitrationPriceForNow"
                                    placeholder="Örn: 1000,50"
                                    value={displayDecimal(arbitrationPriceForNow)}
                                    onChange={(e) => setApplicationFeeField('arbitrationPriceForNow', setArbitrationPriceForNow, normalizeFieldValue('arbitrationPriceForNow', e.target.value))}
                                    invalid={!!applicationFeeErrors.arbitrationPriceForNow}
                                />
                                <FormFeedback>{applicationFeeErrors.arbitrationPriceForNow}</FormFeedback>
                            </FormGroup>
                        </Col>
                    </Row>
                </ModalBody>
                <ModalFooter>
                    <Btn attrBtn={{ color: 'secondary', onClick: closeApplicationFeeModal, disabled: isSubmittingApplicationFee }}>
                        İptal
                    </Btn>
                    <Btn attrBtn={{
                        color: 'primary',
                        onClick: handleSubmitApplicationFee,
                        disabled: isSubmittingApplicationFee
                    }}>
                        {isSubmittingApplicationFee ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                Kaydediliyor...
                            </>
                        ) : (
                            'Kaydet'
                        )}
                    </Btn>
                </ModalFooter>
            </Modal>

            {/* Tahsilat Listesi Modalı */}
            <PaymentCollectionListModal
                isOpen={isPaymentCollectionListModalOpen}
                toggle={closePaymentCollectionListModal}
                title={`Tahsilatlar${currentAccidentFileForPayments?.fileNumber ? ` - Dosya ${currentAccidentFileForPayments.fileNumber}` : ''}`}
                paymentCollections={paymentCollections}
                isLoading={isPaymentCollectionsLoading}
                submittingId={finalCompensationSubmittingId}
                onEdit={handleEditPaymentCollection}
                onSendToCover={handleSendPaymentCollectionToCover}
                onAdd={handleAddPaymentCollection}
                isSendToCoverDisabled={(collection, isSubmitting) => isPaymentCollectionsLoading || !currentAccidentFileForPayments?.id || isSubmitting}
            />

            {/* Tahsilat Ekle/Düzenle Modalı */}
            <PaymentCollectionDataModal
                apiEndpoint={paymentCollectionApiUrl}
                isOpen={paymentCollectionModalOpen}
                toggle={togglePaymentCollectionModal}
                mode={paymentCollectionModalMode}
                selectedPaymentCollection={selectedPaymentCollection}
                refreshData={refreshPaymentCollections}
                accidentFile={currentAccidentFileForPayments?.id ? [String(currentAccidentFileForPayments.id)] : []}
                callForm={currentAccidentFileForPayments?.callForm
                    ? (Array.isArray(currentAccidentFileForPayments.callForm)
                        ? currentAccidentFileForPayments.callForm.map(String)
                        : [String(currentAccidentFileForPayments.callForm)])
                    : []}
                enforcement={[]}
            />
        </Container>
    );
};

export default TodoTabArbitrationReady;
